import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface SiteListingRow {
  justimmo_id: number;
  objekt_nr: string | null;
  title: string | null;
  category: string | null;
  transaction: 'Kauf' | 'Miete';
  price: number | null;
  area: number | null;
  rooms: number | null;
  plz: string | null;
  ort: string | null;
  street_released: string | null;
  location_hint: string | null;
  agent_name: string | null;
  agent_email: string | null;
  agent_phone: string | null;
  images: string[];
  site_url: string;
  first_seen_at: number | string;
  gone_at: number | string | null;
  state: 'linked' | 'pending' | 'site_only';
  /** Vom Server: darf diese Person das Inserat übernehmen (Zustand separat). */
  can_add: boolean;
  linked_property: {
    id: string;
    address: string | null;
    assigned_agent_name: string | null;
    primary_image_url: string | null;
  } | null;
}

export interface SiteListingCounts {
  on_site: number;
  site_only: number;
  not_on_site: number;
  all: number;
}

export interface SiteListingFilters {
  segment: 'on_site' | 'site_only';
  scope?: 'mine' | 'all';
  search?: string;
}

export function useSiteListingCounts() {
  return useQuery({
    queryKey: ['site-listings', 'counts'] as const,
    queryFn: async () => {
      const res = await apiFetch('/site-listings/counts');
      if (!res.ok) throw new Error(`site_listing_counts_failed_${res.status}`);
      return (await res.json()) as SiteListingCounts;
    },
    staleTime: 30_000,
  });
}

export function useInfiniteSiteListings(filters: SiteListingFilters, options: { enabled?: boolean } = {}, pageSize = 50) {
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: ['site-listings', 'infinite', { ...filters, pageSize }] as const,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        segment: filters.segment,
        limit: String(pageSize),
        offset: String(pageParam ?? 0),
      });
      if (filters.scope) params.set('scope', filters.scope);
      if (filters.search) params.set('search', filters.search);
      const res = await apiFetch(`/site-listings?${params}`);
      if (!res.ok) throw new Error(`site_listings_list_failed_${res.status}`);
      const json = (await res.json()) as { items: SiteListingRow[]; total: number };
      return { ...json, offset: (pageParam ?? 0) as number };
    },
    initialPageParam: 0 as number,
    getNextPageParam: (last) => (last.offset + last.items.length < last.total && last.items.length > 0 ? last.offset + pageSize : undefined),
    staleTime: 30_000,
  });
}

/** Alle aktuellen Inserate auf einmal (bis 200), um im Bereich „Alle" verbundenen
 *  Objekten ohne eigenes Foto das Bild des Inserats zu geben. */
export function useSiteListingImagesByProperty(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['site-listings', 'images-by-property'] as const,
    queryFn: async () => {
      const res = await apiFetch('/site-listings?segment=on_site&limit=200');
      if (!res.ok) throw new Error(`site_listings_list_failed_${res.status}`);
      const json = (await res.json()) as { items: SiteListingRow[] };
      const map = new Map<string, string>();
      for (const l of json.items) {
        if (l.linked_property && l.images[0]) map.set(l.linked_property.id, l.images[0]);
      }
      return map;
    },
    staleTime: 60_000,
  });
}

export function useSiteListingByProperty(propertyId: string | undefined) {
  return useQuery({
    enabled: !!propertyId,
    queryKey: ['site-listings', 'by-property', propertyId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/site-listings/by-property/${propertyId}`);
      if (!res.ok) throw new Error(`site_listing_by_property_failed_${res.status}`);
      return (await res.json()) as SiteListingRow | null;
    },
    staleTime: 60_000,
  });
}

export interface AddListingToCrmPayload {
  client: {
    title: string;
    firstName?: string | null;
    lastName: string;
    phone: string;
    email?: string | null;
  };
  address?: {
    city?: string | null;
    postalCode?: string | null;
    street?: string | null;
    streetNumber?: string | null;
    entrance?: string | null;
    floor?: string | null;
    doorNumber?: string | null;
    district?: string | null;
  };
  agent_profile_id?: string | null;
}

export function useAddListingToCrm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: AddListingToCrmPayload }) => {
      const res = await apiFetch(`/site-listings/${id}/add-to-crm`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        throw new Error(err?.error ?? err?.message ?? `add_to_crm_failed_${res.status}`);
      }
      return (await res.json()) as { property_id: string; client_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-listings'] });
      qc.invalidateQueries({ queryKey: ['properties'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

// ─── Admin: Vorschläge, Entscheidungen, Abgleich ──────────────────────

export interface ProposalCandidate {
  proposal_id: string;
  id: string;
  rank: number;
  score_reason: string | null;
  address: string | null;
  purchase_price: number | null;
  property_size: number | null;
  primary_image_url: string | null;
  assigned_agent_name: string | null;
}

export interface ProposalListing {
  justimmo_id: number;
  objekt_nr: string | null;
  title: string | null;
  transaction: 'Kauf' | 'Miete';
  price: number | null;
  area: number | null;
  plz: string | null;
  ort: string | null;
  street_released: string | null;
  location_hint: string | null;
  agent_name: string | null;
  images: string[];
  site_url: string;
}

export interface ProposalItem {
  listing: ProposalListing;
  candidates: ProposalCandidate[];
}

async function failWith(res: Response, fallback: string): Promise<never> {
  const err = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
  throw new Error(err?.error ?? err?.message ?? `${fallback}_${res.status}`);
}

export function useListingProposals() {
  return useQuery({
    queryKey: ['site-listings', 'proposals'] as const,
    queryFn: async () => {
      const res = await apiFetch('/site-listings/proposals');
      if (!res.ok) throw new Error(`site_listing_proposals_failed_${res.status}`);
      return ((await res.json()) as { items: ProposalItem[] }).items;
    },
    staleTime: 15_000,
  });
}

/** Verbundene Inserate (zum Auflösen einer Verbindung); serverseitig gesucht, nie alles geladen. */
export function useLinkedListings(search: string) {
  return useQuery({
    queryKey: ['site-listings', 'linked', search] as const,
    queryFn: async () => {
      const params = new URLSearchParams({ segment: 'linked', scope: 'all', limit: '30' });
      if (search) params.set('search', search);
      const res = await apiFetch(`/site-listings?${params}`);
      if (!res.ok) throw new Error(`site_listings_list_failed_${res.status}`);
      return ((await res.json()) as { items: SiteListingRow[] }).items;
    },
    staleTime: 15_000,
  });
}

export function useListingDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { id: number; action: 'link'; propertyId: string } | { id: number; action: 'no-match' | 'unlink' },
    ) => {
      const res = await apiFetch(`/site-listings/${args.id}/${args.action}`, {
        method: 'POST',
        body: args.action === 'link' ? JSON.stringify({ property_id: args.propertyId }) : undefined,
      });
      if (!res.ok) await failWith(res, 'site_listing_decision_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-listings'] });
      qc.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export interface ListingPairRow {
  justimmo_id: number;
  listing_address: string | null;
  listing_plz: string | null;
  listing_price: number | null;
  property_id: string;
  property_address: string | null;
  property_price: number | null;
  reason: string;
  rank?: number;
}

export interface ListingSyncReport {
  run_id: string | null;
  status: 'ok' | 'partial' | 'failed' | 'skipped';
  dry_run: boolean;
  seen: number;
  created: number;
  updated: number;
  gone: number;
  returned: number;
  auto_linked: number;
  conflicts: number;
  proposals: number;
  no_candidate: number;
  guard_tripped: boolean;
  shrink_accepted: boolean;
  error: string | null;
  details: { auto_pairs: ListingPairRow[]; proposals: ListingPairRow[] };
}

export function useListingSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { dryRun: boolean; acceptShrink?: boolean }) => {
      const res = await apiFetch('/justimmo/listings/sync', {
        method: 'POST',
        body: JSON.stringify({ dry_run: args.dryRun, ...(args.acceptShrink ? { accept_shrink: true } : {}) }),
      });
      if (!res.ok) await failWith(res, 'listing_sync_failed');
      return (await res.json()) as ListingSyncReport;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['listing-runs'] });
      if (!r.dry_run) {
        qc.invalidateQueries({ queryKey: ['site-listings'] });
        qc.invalidateQueries({ queryKey: ['properties'] });
      }
    },
  });
}

export interface ListingRunRow {
  id: string;
  kind: string;
  trigger: string | null;
  status: string;
  started_at: number | string | null;
  seen: number | null;
  created: number | null;
  error: string | null;
}

export function useListingRuns() {
  return useQuery({
    queryKey: ['listing-runs'] as const,
    queryFn: async () => {
      const res = await apiFetch('/justimmo/runs?limit=40');
      if (!res.ok) throw new Error(`justimmo_runs_failed_${res.status}`);
      const items = ((await res.json()) as { items: ListingRunRow[] }).items;
      return items.filter((r) => r.kind === 'rest_listings').slice(0, 3);
    },
    staleTime: 15_000,
  });
}
